import {
	AgreementTypeEnum,
	IndexationType,
	TimeUnit,
	TimeWindow,
	TimeWindowInput,
} from "__generated__/graphql";
import { round } from "utils/numberUtils";
import { CharterSubtype } from "__generated__/enums";
import { ContractOptionFormInput } from "view/contractsNew/validation/contractoption.zod";
import { AgreementFormOutput } from "view/agreements/validation/agreement.zod";

export const parentContractCompatibleSubtypes: readonly CharterSubtype[] = [ CharterSubtype.Time, CharterSubtype.Contract ];

export const preHandleAgreements = (agreements: AgreementFormOutput[]) => {
	const allAgreements = [ ...agreements ];

	for (const agreement of agreements) {
		if (agreement.id != null || agreement.type !== AgreementTypeEnum.Feet) {
			continue;
		}

		// Expand feet agreements (previously done in backend)
		const items = agreement.calculationMethod?.staffel?.staffelItems ?? [];
		if (items.length === 0) {
			continue;
		}

		for (const item of items) {
			allAgreements.push({
				...agreement,
				id: undefined,
				uuid: crypto.randomUUID(),
				primary: false,
				calculationMethod: {
					...agreement.calculationMethod,
					staffelItem: item,
					staffel: undefined,
				},
			});
		}
	}

	allAgreements.forEach((agreement) => {
		agreement.priority = allAgreements
			.filter(a => a.type === agreement.type)
			.findIndex(a => (a.id != null && agreement.id === a.id) || agreement.uuid === a.uuid) + 1;
	});

	return allAgreements;
};

export const getPricePerUnit = (previousOptionIndex: number | null, indexedPrices: Map<string | number, number>, agreementPricePerUnit: number | null): number | null => {
	if (previousOptionIndex === null) {
		// if this is the first option
		return agreementPricePerUnit;
	} else if (previousOptionIndex > -1) {
		return indexedPrices.get(previousOptionIndex) ?? null;
	} else {
		// if `previous option` is unknown
		return null;
	}
};

type Agreement = {
	type: AgreementTypeEnum | null;
	calculationMethod: {
		price?: number | null;
		timeUnit?: TimeUnit | null;
	} | null;
}

export const getPriceUnitAndPricePerUnitFromRentAgreement = (agreements: Agreement[]): [ number | null, TimeUnit | null ] => {
	const rentAgreement: Agreement | undefined = agreements
		.find((agreement) => agreement.type === AgreementTypeEnum.Rent);

	const pricePerUnit = rentAgreement?.calculationMethod?.price || null;
	const priceUnit =  rentAgreement?.calculationMethod?.timeUnit || null;

	return [ pricePerUnit, priceUnit ];
};

export const calculateIndexedPricePerUnit =
	(currentOption: ContractOptionFormInput | null): number | null => {
		if (!currentOption?.indexationType || currentOption.pricePerUnit === null || currentOption.pricePerUnit === undefined) {
			return null;
		}

		switch (currentOption.indexationType) {
			case IndexationType.FixedAmount:
				if (!currentOption.indexationAmount) {
					return null;
				}
				return round(Number(currentOption.pricePerUnit) + Number(currentOption.indexationAmount));
			case IndexationType.FixedPercentage:
			case IndexationType.CpiPercentage:
				if (!currentOption.indexationAmount) {
					return null;
				}
				return round((currentOption.pricePerUnit * (1 + currentOption.indexationAmount / 100)));
			case IndexationType.None:
				return currentOption.pricePerUnit ?? null;
			default:
				console.warn("Unsupported IndexationType");
				return null;
		}
	};

export const createIndexedPriceMap = (options?: ContractOptionFormInput[]) => {
	return new Map<number, number>(options?.flatMap((option, index) => {
		const indexedPrice = calculateIndexedPricePerUnit(option);
		if (!indexedPrice) {
			return [];
		}
		return [ [ index, indexedPrice ] ];
	}));
};

export const sortOptions = (options?: ContractOptionFormInput[]): ContractOptionFormInput[] => {
	return options?.filter((option): option is ContractOptionFormInput & { validityWindow: { from: number } } => !!option.validityWindow?.from)
		.toSorted((a, b) => a.validityWindow.from - b.validityWindow.from) ?? [];
};

export const timeWindowsOverlap = (firstWindow: TimeWindow | TimeWindowInput, secondWindow: TimeWindow | TimeWindowInput): boolean => {
	if (!firstWindow.from) {
		if (!firstWindow.to) {
			// Both are not defined, there is no overlap
			return false;
		}
		// If secondWindow.to is defined, then they always overlap
		return !!secondWindow.to;
	} else if (!firstWindow.to) {
		// If firstWindow.to is not defined, while both firstWindow.from and secondWindow.from are defined,
		//  then they always overlap
		return !!secondWindow.from;
	} else {
		// firstWindow.from and firstWindow.to are defined
		if (!secondWindow.from) {
			// If secondWindow.to is defined, there is overlap
			return !!secondWindow.to;
		} else if (!secondWindow.to) {
			// Since secondWindow.from is defined, there is overlap
			return true;
		}

		// All are defined, compare normally
		return firstWindow.to >= secondWindow.from && firstWindow.from <= secondWindow.to;
	}
};

export const timeWindowOverlaps = (window: TimeWindow | TimeWindowInput, windowsArray: Array<TimeWindow | TimeWindowInput>, allowOverlaps?: number): boolean => {
	if (!allowOverlaps) {
		return windowsArray.some(windowFromArray => timeWindowsOverlap(window, windowFromArray));
	}

	return windowsArray
		.filter(windowFromArray => timeWindowsOverlap(window, windowFromArray))
		.length > allowOverlaps;
};
